import { EMPTY_ARRAY } from 'constants/defaults';
import { TIMELINE_pixelsPerMinute, TIMESTAMP_SNAPPING_MINUTES } from 'constants/user';
import { cloneDeep } from 'lodash-es';
import { DateTime } from 'luxon';
import { normalize } from 'normalizr';
import { xhrStatusFactory } from 'redux/reusable/xhrStatus';
import { TIMELINE_SCHEMA_KEY, timelineListSchema } from 'schema/timeline';
import type { Entry, SuggestedEntry } from 'types/timeline';
import { reducerWithInitialState } from 'typescript-fsa-reducers';
import { isSameDay } from 'utils/date-helper';
import {
  createMemoryGroupRef,
  extractProjectPredictionOnEntryIdFromSuggestedEntries,
  extractProjectPredictionsFromSuggestedEntries,
} from 'utils/timeline';
import type { State } from './timeline.types';
import * as types from './timeline.types';
import { TimelineSortTypes, timelineXhrModule } from './timeline.types';
import * as userTypes from './user.types';

const DEFAULT_SNAPPING_MINUTES = 5;

export const initialState: State = {
  // Not normalizable, as groups don't have ids
  groups: EMPTY_ARRAY,
  projectPredictions: {},
  entryIdPredictions: {},
  entities: {
    [TIMELINE_SCHEMA_KEY]: {},
  },
  result: EMPTY_ARRAY,
  resultMeta: {
    fetchedForDate: '',
    fetchingForDate: '',
    excludedTrackerEntries: null,
  },
  selection: {
    entryIds: EMPTY_ARRAY,
    // groupIds
  },
  lastPusherUpdate: null,
  filter: {
    displayTimelineRules: ['other', 'selected', 'logged'],
    displayGroupRules: ['other', 'selected'],
    searchQuery: '',
    expandedEntryIds: EMPTY_ARRAY,
    expandGroupRefs: EMPTY_ARRAY,
    noiseMinThreshold: 0, // Memories below this threshold are considered "noise"
    importanceMinThreshold: 0, // (0-1)
    // Potentially overwritten by users preference:
    // See selectors/timeline::getFilter
    mode: 'group', // timeline
    // Overwritten by user account preference
    timeFormat: 24,
    timelineSort: TimelineSortTypes.sortByGroupDuration,
  },
  pixelsPerMinute: 2,
  timestampSnappingMinutes: DEFAULT_SNAPPING_MINUTES,
  scrollPostion: null,
  xhrStatus: xhrStatusFactory(),
  showAiIgnoredMemoriesModal: false,
};

const updatePixelsPerMinute = (state: State, pixelsPerMinute: number): State => {
  return state.pixelsPerMinute === pixelsPerMinute
    ? state
    : {
        ...state,
        pixelsPerMinute: Math.max(1, Math.min(pixelsPerMinute, 6)),
      };
};

const updateTimestampSnappingMinutes = (state: State, timestampSnappingMinutes: number): State => {
  const newValue = types.ALLOWED_TIMESTAMP_SNAPPING_VALUES.includes(timestampSnappingMinutes)
    ? timestampSnappingMinutes
    : DEFAULT_SNAPPING_MINUTES;
  return state.timestampSnappingMinutes === newValue
    ? state
    : {
        ...state,
        timestampSnappingMinutes: newValue,
      };
};

const reducer = reducerWithInitialState<State>(initialState)
  .caseWithAction(types.toggleSelectEntryIds, (state, action): State => {
    const { payload } = action;
    const existingEntryIds = state.selection.entryIds;
    const alreadySelected =
      existingEntryIds.length && payload.entry_ids.every(id => existingEntryIds.includes(id));

    const entryIds = alreadySelected
      ? existingEntryIds.filter(id => !payload.entry_ids.includes(id))
      : [...existingEntryIds, ...payload.entry_ids];

    return {
      ...state,
      selection: {
        ...state.selection,
        entryIds,
      },
    };
  })
  .case(types.clearSelectedEntryIds, (state): State => {
    return {
      ...state,
      selection: {
        ...state.selection,
        entryIds: [],
      },
    };
  })
  .caseWithAction(types.setFilter, (state, action): State => {
    const newPartialState = {
      [action.payload.key]: action.payload.value,
    };
    if (action.payload.key === 'mode') {
      // Need to reset expanded/collapsed items state
      newPartialState.expandGroupRefs = [];
    }
    return {
      ...state,
      filter: {
        ...state.filter,
        ...newPartialState,
      },
    };
  })
  .caseWithAction(types.setTimelineStyle, (state, action): State => {
    return {
      ...state,
      style: action.payload,
    };
  })
  .caseWithAction(types.toggleExpandGroupRef, (state, action): State => {
    const existingRefs = state.filter.expandGroupRefs;
    const expandGroupRefs = existingRefs.includes(action.payload)
      ? existingRefs.filter(ref => ref !== action.payload)
      : [...existingRefs, action.payload];

    return {
      ...state,
      filter: {
        ...state.filter,
        expandGroupRefs,
      },
    };
  })
  .caseWithAction(types.toggleExpandEntryId, (state, action): State => {
    const existingEntryIds = state.filter.expandedEntryIds;
    const expandedEntryIds = existingEntryIds.includes(action.payload)
      ? existingEntryIds.filter(id => id !== action.payload)
      : [...existingEntryIds, action.payload];

    return {
      ...state,
      filter: {
        ...state.filter,
        expandedEntryIds,
      },
    };
  })
  .caseWithAction(types.markEntryIdsAsSpam, (state, action): State => {
    // Turn `spam` flag on for affected entry ids
    const entitiesTimelineCopy = cloneDeep(state.entities[TIMELINE_SCHEMA_KEY]);
    action.payload.entry_ids.forEach(id => {
      if (entitiesTimelineCopy[id]) {
        entitiesTimelineCopy[id].spam = true;
      }
    });

    return {
      ...state,
      entities: {
        ...state.entities,
        [TIMELINE_SCHEMA_KEY]: entitiesTimelineCopy,
      },
    };
  })

  .caseWithAction(types.markEntryIdsAsNotSpam, (state, action): State => {
    // Turn `spam` flag off for affected entry ids
    const entitiesTimelineCopy = cloneDeep(state.entities[TIMELINE_SCHEMA_KEY]);
    action.payload.entry_ids.forEach(id => {
      if (entitiesTimelineCopy[id]) {
        entitiesTimelineCopy[id].spam = false;
      }
    });

    return {
      ...state,
      entities: {
        ...state.entities,
        [TIMELINE_SCHEMA_KEY]: entitiesTimelineCopy,
      },
    };
  })
  .caseWithAction(types.deleteEntryIds, (state, action): State => {
    const result = state.result.filter(id => !action.payload.entry_ids.includes(id));
    return {
      ...state,
      result: result.length ? result : EMPTY_ARRAY,
    };
  })
  .caseWithAction(types.suggestedEntriesPusherUpdate, (state): State => {
    return {
      ...state,
      lastPusherUpdate: DateTime.now().toISO(),
    };
  })
  .caseWithAction(types.fetchingSuggestedEntries, (state, action): State => {
    const { date } = action.payload;
    const hasMemoriesOnDate = state.result.some((id: number) => {
      const memory = state.entities[TIMELINE_SCHEMA_KEY][id];
      return isSameDay(memory.from, date) || isSameDay(memory.to, date);
    });

    return {
      ...state,
      projectPredictions: {},
      entryIdPredictions: {},
      // Clear local data unless we're re-fetching same data:
      groups: hasMemoriesOnDate ? state.groups : EMPTY_ARRAY,
      result: hasMemoriesOnDate ? state.result : EMPTY_ARRAY,
      resultMeta: {
        ...state.resultMeta,
        fetchingForDate: date,
      },
    };
  })
  .caseWithAction(types.fetchedSuggestedEntries, (state, action): State => {
    const { entries, date, excludeTrackerEntries } = action.payload;

    // Extract entries from suggested_entries
    const flattened: Array<Entry> = entries.reduce(
      (prev: Array<Entry>, suggestedEntry: SuggestedEntry) => {
        prev.push(...suggestedEntry.entries);
        return prev;
      },
      []
    );

    const { entities, result } = normalize<typeof entries, types.DefaultEntities>(
      flattened,
      timelineListSchema
    );

    // WARNING
    // This much be moved to selectors for the timeline.actions::predictEntryIdsToProjectId
    // changes to have any effect. I'm waiting with this, as I'm really hoping the "predicted project" indicator
    // will be moved to objects from /timeline.json
    const entryIdPredictions = extractProjectPredictionOnEntryIdFromSuggestedEntries(entries);
    const projectPredictions = extractProjectPredictionsFromSuggestedEntries(entries);

    return {
      ...state,
      projectPredictions,
      entryIdPredictions,
      groups: entries.map((group: Omit<SuggestedEntry, 'ref'>) => ({
        ref: createMemoryGroupRef(group.entry_ids, group.title),
        ...group,
      })),
      entities: entries.length ? entities : state.entities,
      result: result.length ? result : EMPTY_ARRAY,
      resultMeta: {
        ...state.resultMeta,
        fetchingForDate: '',
        fetchedForDate: date,
        excludedTrackerEntries: excludeTrackerEntries,
      },
    };
  })
  .caseWithAction(types.predictGroupRefsToProjectId, (state, action): State => {
    const { groupRefs, projectId } = action.payload;

    return {
      ...state,
      groups: state.groups.map(group => {
        if (groupRefs.includes(group.ref)) {
          return {
            ...group,
            projects: [
              {
                project_id: projectId,
                accuracy: 1,
                count: group.entry_ids.length,
              },
              // Remove any existing predictions, just in case
              ...group.projects.filter(({ project_id }) => project_id !== projectId),
            ],
          };
        }
        return group;
      }),
    };
  })
  .caseWithAction(types.preserveScrollPosition, (state, action): State => {
    return {
      ...state,
      scrollPostion: action.payload,
    };
  })
  .caseWithAction(types.setGutterHighlight, (state, action): State => {
    return {
      ...state,
      gutterHighlight: action.payload || undefined,
    };
  })
  .case(types.setPixelsPerMinute, updatePixelsPerMinute)
  .case(types.setTimestampSnappingMinutes, updateTimestampSnappingMinutes)
  .cases([userTypes.setUserProperties, userTypes.setUserProperty], (state, properties): State => {
    const maybePixelsPerMinute = properties[TIMELINE_pixelsPerMinute];
    state = isNaN(maybePixelsPerMinute as number)
      ? state
      : updatePixelsPerMinute(state, +(maybePixelsPerMinute as number));

    const maybeTimestampSnappingMinutes = properties[TIMESTAMP_SNAPPING_MINUTES];
    state = isNaN(maybeTimestampSnappingMinutes as number)
      ? state
      : updateTimestampSnappingMinutes(state, +(maybeTimestampSnappingMinutes as number));

    return state;
  })
  .case(types.setShowAiIgnoredMemoriesModal, (state, payload): State => {
    if (payload === state.showAiIgnoredMemoriesModal) {
      return state;
    }

    return {
      ...state,
      showAiIgnoredMemoriesModal: payload,
    };
  });

export default timelineXhrModule.withXhrStatusReducer(reducer);
